// src/components/ResponsiveNavBar.jsx
import React, { useState, useEffect } from 'react';
import {
  AppBar, Toolbar, IconButton, Drawer, List, ListItemButton,
  ListItemIcon, ListItemText, Box, Button, Divider, useMediaQuery,
  Menu, MenuItem, Typography
} from '@mui/material';
import {
  Menu as MenuIcon,
  Close as CloseIcon,
  Groups as GroupsIcon,
  Storefront as StorefrontIcon,
  ContactMail as ContactMailIcon,
  Timeline as TimelineIcon,
  ArrowDropDown as ArrowDropDownIcon,
  PersonAddAlt1 as PersonAddAlt1Icon,
  Language as LanguageIcon,
  SupportAgent as SupportAgentIcon
} from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import i18n from '../i18n';
import SocialIcons from './SocialIcons';

const drawerWidth = 260;

export default function ResponsiveNavBar({ onNavHover }) {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [langAnchor, setLangAnchor] = useState(null);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const location = useLocation();
  const navigate = useNavigate();
  const { t } = useTranslation();

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const toggleDrawer = () => setOpen(!open);
  const handleMenuOpen = (e) => setAnchorEl(e.currentTarget);
  const handleMenuClose = () => setAnchorEl(null);
  const goAlta = () => {
    navigate('/alta');
    setOpen(false);
  };

  const handleScrollNav = (sectionId) => {
    navigate('/club');
    setTimeout(() => {
      document.getElementById(sectionId)?.scrollIntoView({ behavior: 'smooth' });
    }, 100);
    setOpen(false);
  };

  const openLangMenu = (e) => setLangAnchor(e.currentTarget);
  const closeLangMenu = () => setLangAnchor(null);
  const changeLanguage = (lng) => {
    i18n.changeLanguage(lng);
    localStorage.setItem('lang', lng);
    closeLangMenu();
  };

  const drawerItems = [
    { label: t('recap'), href: '/recap', icon: <TimelineIcon /> },
    { label: t('tienda'), href: '/tienda', icon: <StorefrontIcon /> },
    { label: t('alta'), href: '/alta', icon: <PersonAddAlt1Icon /> },
    { label: t('contacto'), href: '/contacto', icon: <ContactMailIcon /> },
    { label: t('soporte'), href: '/soporte', icon: <SupportAgentIcon /> },
    {
      label: '',
      href: '/eventos',
      icon: <img src="/10klogo.png" alt="10K Puerto de Cartagena" style={{ height: 32, width: 'auto', marginTop: 2 }} />,
      isImage: true,
    },
  ];

  const drawerContent = (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 2, py: 1, ...theme.mixins.toolbar }}>
        <Link to="/" onClick={toggleDrawer}><img src="/runtriLogoCtt.PNG" alt="Runtriton" height={80} /></Link>
        <IconButton onClick={toggleDrawer} aria-label="close drawer"><CloseIcon /></IconButton>
      </Box>
      <Divider />
      <List sx={{ flexGrow: 1 }}>
        <Typography variant="subtitle2" sx={{ pl: 2, pt: 1, fontWeight: 'bold', color: 'text.secondary' }}>
          {t('CLUB')}
        </Typography>
        <ListItemButton onClick={() => handleScrollNav('timeline')}>
          <ListItemIcon><GroupsIcon /></ListItemIcon>
          <ListItemText primary={t('club.historia')} />
        </ListItemButton>
        <ListItemButton onClick={() => handleScrollNav('directiva')}>
          <ListItemIcon><GroupsIcon /></ListItemIcon>
          <ListItemText primary={t('club.directiva')} />
        </ListItemButton>
        <ListItemButton onClick={goAlta}>
          <ListItemIcon><PersonAddAlt1Icon /></ListItemIcon>
          <ListItemText primary={t('club.unete')} />
        </ListItemButton>

        {drawerItems.map(({ label, href, icon, isImage }) => (
          <ListItemButton
            key={href}
            component={Link}
            to={href}
            onClick={toggleDrawer}
            selected={location.pathname === href || location.pathname.startsWith(`${href}/`)}
            aria-label={isImage ? '10K Puerto de Cartagena' : label}
            sx={{
              '&.Mui-selected': {
                fontWeight: 'bold',
                borderLeft: `4px solid ${theme.palette.primary.main}`,
                bgcolor: 'action.selected',
              },
            }}
          >
            <ListItemIcon sx={{ minWidth: isImage ? 48 : 40 }}>{icon}</ListItemIcon>
            <ListItemText primary={isImage ? null : label} />
          </ListItemButton>
        ))}
      </List>
      <Box sx={{ p: 2 }}><SocialIcons centered /></Box>
    </Box>
  );

  const currentLangLabel = i18n.language === 'en' ? 'English' : 'Español';
  const appBarHeight = isMobile ? (scrolled ? 66 : 84) : (scrolled ? 76 : 112);
  const logoHeight = isMobile ? (scrolled ? 44 : 58) : (scrolled ? 54 : 86);

  const navButtonSx = (active = false) => ({
    color: active ? 'primary.main' : 'text.primary',
    fontWeight: 900,
    fontSize: 15,
    letterSpacing: '0.04em',
    px: 1.8,
    py: 0.95,
    borderRadius: 999,
    textTransform: 'uppercase',
    backgroundColor: active ? 'rgba(229, 9, 20, 0.08)' : 'transparent',
    '&:hover': {
      color: 'primary.main',
      backgroundColor: 'rgba(229, 9, 20, 0.08)',
    },
  });

  return (
      <Box sx={{ flexGrow: 1 }}>
        <AppBar
          position="fixed"
          elevation={0}
          sx={{
            backgroundColor: scrolled ? 'rgba(255,255,255,0.94)' : 'rgba(255,255,255,0.78)',
            backdropFilter: 'blur(18px)',
            borderBottom: '1px solid rgba(17,24,39,0.08)',
            boxShadow: scrolled ? '0 16px 40px rgba(15,23,42,0.08)' : 'none',
            transition: 'height 0.3s ease, background-color 0.3s ease, box-shadow 0.3s ease',
            height: appBarHeight,
            display: 'flex',
            justifyContent: 'center',
          }}
          onMouseEnter={() => onNavHover?.(true)}
          onMouseLeave={() => onNavHover?.(false)}
        >
          <Toolbar sx={{ justifyContent: 'space-between', px: isMobile ? 1.5 : 4, minHeight: 'auto !important' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 2, lg: 3 } }}>
              <Link to="/"><img src="/runtriLogoCtt.PNG" alt="Runtriton Logo" style={{ height: logoHeight, transition: 'height .3s ease' }} /></Link>

              {!isMobile && (
                <>
                  <Button
                    onClick={handleMenuOpen}
                    aria-label="Menú del club"
                    aria-expanded={Boolean(anchorEl)}
                    aria-haspopup="true"
                    sx={navButtonSx(location.pathname === '/club')}
                    endIcon={<ArrowDropDownIcon />}
                  >
                    {t('CLUB')}
                  </Button>
                  <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleMenuClose}>
                    <MenuItem onClick={() => { handleScrollNav('timeline'); handleMenuClose(); }}>{t('club.historia')}</MenuItem>
                    <MenuItem onClick={() => { handleScrollNav('directiva'); handleMenuClose(); }}>{t('club.directiva')}</MenuItem>
                    <MenuItem onClick={() => { navigate('/alta'); handleMenuClose(); }}>{t('club.unete')}</MenuItem>
                  </Menu>

                  {['recap', 'tienda', 'alta', 'contacto', 'soporte'].map((label) => (
                    <Button
                      key={label}
                      component={Link}
                      to={`/${label}`}
                      sx={navButtonSx(location.pathname === `/${label}`)}
                    >
                      {t(label)}
                    </Button>
                  ))}

                  <Button component={Link} to="/eventos" aria-label="10K Puerto de Cartagena" sx={{ minWidth: 48, p: '6px 12px', borderRadius: 999, '&:hover': { bgcolor: 'rgba(229, 9, 20, 0.08)' } }}>
                    <img src="/10klogo.png" alt="10K Puerto de Cartagena" style={{ height: 32 }} />
                  </Button>
                </>
              )}
            </Box>

            {!isMobile && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <SocialIcons />
                <IconButton
                  sx={{ color: '#E50914', bgcolor: 'rgba(229, 9, 20, 0.08)', '&:hover': { bgcolor: 'rgba(229, 9, 20, 0.14)' } }}
                  onClick={openLangMenu}
                  aria-label={`Idioma actual: ${currentLangLabel}. Cambiar idioma`}
                  aria-expanded={Boolean(langAnchor)}
                  aria-haspopup="true"
                >
                  <LanguageIcon />
                </IconButton>
                <Menu anchorEl={langAnchor} open={Boolean(langAnchor)} onClose={closeLangMenu}>
                  <MenuItem onClick={() => changeLanguage('es')}>{t('lang.es')}</MenuItem>
                  <MenuItem onClick={() => changeLanguage('en')}>{t('lang.en')}</MenuItem>
                </Menu>
              </Box>
            )}

            {isMobile && (
              <IconButton edge="end" onClick={toggleDrawer} aria-label="open drawer" sx={{ bgcolor: 'rgba(16,24,40,0.06)', color: '#101828', '&:hover': { bgcolor: 'rgba(229,9,20,0.10)' } }}>
                <MenuIcon />
              </IconButton>
            )}
          </Toolbar>
        </AppBar>

        <Drawer
          anchor="left"
          open={open}
          onClose={toggleDrawer}
          ModalProps={{ keepMounted: true }}
          sx={{ display: { xs: 'block', md: 'none' }, '& .MuiDrawer-paper': { width: drawerWidth, background: 'linear-gradient(180deg, #ffffff 0%, #F7F8FB 100%)' } }}
        >
          {drawerContent}
        </Drawer>
      </Box>
  );
}
